import React, { useMemo, useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import { Alert, Box, CircularProgress, Link, Stack, Typography } from '@mui/material';

import { useGetDeparturesInventory } from '~/hooks/apis/tours/useTourInventory';
import { useGetTourOfferSummary } from '~/hooks/apis/tours/useTourOffers';

import { TourFormDeparture } from '~/services/ToursService';

import { TourService } from '~/types/tours/costPrice';

import TourDepartureSelector from './TourDepartureSelector';
import TourInventorySection from './TourInventorySection';
import { configurationOrder } from './departureInventory';
import { groupDeparturesForSelector } from './departureRows';
import { pricingAndInventoryPath } from './pricingAndInventoryTab';
import useInventorySnapshot from './useInventorySnapshot';

interface Props {
  /** The LTLE's tour service, linked to the tour offer it is built on. */
  service: TourService;
  departures: Array<TourFormDeparture>;
  isLoadingDepartures: boolean;
  departuresError?: { message: string } | null;
}

/**
 * An LTLE is costed on the tour it is built on, whose pricing and inventory show its
 * bookings alongside everyone else's; here is only what was booked on the LTLE.
 */
export default function LtleBookings({ service, departures, isLoadingDepartures, departuresError }: Props) {
  const { data: parent } = useGetTourOfferSummary(service.linkedTourOfferId);
  const [departureKey, setDepartureKey] = useState('');

  const departureGroups = useMemo(() => groupDeparturesForSelector(departures), [departures]);
  const selectedDepartures = useMemo(
    () => departureGroups.find(([key]) => key === departureKey)?.[1] ?? [],
    [departureGroups, departureKey],
  );

  const {
    data: inventory,
    isLoading: isLoadingInventory,
    error: inventoryError,
  } = useGetDeparturesInventory({
    serviceId: service.id,
    packageDepartureIds: selectedDepartures.flatMap((departure) => departure.packageDepartureId ?? []),
  });
  const { date: snapshotDate, onDateChange } = useInventorySnapshot(inventory);

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Alert severity="info">
        This LTLE is built on{' '}
        {parent?.legacyTourId ? (
          <Link component={RouterLink} to={pricingAndInventoryPath(parent.legacyTourId)} fontWeight={600}>
            {parent.name ?? 'its base tour'}
          </Link>
        ) : (
          <strong>{parent?.name ?? 'its base tour'}</strong>
        )}
        , and its bookings are costed on that tour&apos;s sliding scale. Cost prices and everyone on each departure are
        shown there; below are this LTLE&apos;s bookings.
      </Alert>

      {isLoadingDepartures && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!!departuresError && (
        <Alert severity="error">Could not load this tour&apos;s departures: {departuresError.message}</Alert>
      )}

      {!isLoadingDepartures && !departuresError && (
        <>
          <TourDepartureSelector
            departures={departures}
            selectedDepartureKey={departureKey}
            onSelectDeparture={setDepartureKey}
          />

          <Typography variant="h4" fontWeight={600}>
            Bookings
          </Typography>

          <TourInventorySection
            departures={departures}
            selectedDepartureId={departureKey}
            inventory={inventory}
            tourOfferId={service.tourOfferId}
            order={configurationOrder(service)}
            snapshotDate={snapshotDate}
            onDateChange={onDateChange}
            items={[]}
            showCosts={false}
            showCostChart
            isLoading={isLoadingInventory}
            error={inventoryError}
          />
        </>
      )}
    </Stack>
  );
}
